/**
 * ลานศิลาเก้ากระบี่, the hub city inside the Yanyu region, as a greybox (docs/archive/HUB.md option ก,
 * นครกลางลำน้ำ; docs/archive/REGION_GREYBOX.md). Ported from the approved plan's layout
 * (`scripts/layouts/hub-layout.mjs`, `buildHub('A')`): walls 880 × 740 m, 9 m high, with five gates and two
 * barbicans; ถนนหลวง (40 m) and the east–west avenue (30 m) crossing at ลานศิลาใหญ่ (260 × 200 m);
 * ลำน้ำศิลา through the east third with quays and three bridges (สะพานรุ้ง carries the avenue);
 * compounds in gardens, the markets, the martial quarter, the lakes; the fair, the south suburb,
 * the boss's ground and the pagoda on its hill outside.
 *
 * Plain boxes on the sketch's 22 m plain: detail lanes replace them by id (the `lane` field). Data
 * only: the loops below lay out repeated boxes (wall runs, quays, the garden compounds), all from
 * fixed numbers and a coordinate hash, so the list is the same every run.
 */
import type { Facing } from '../../map';
import { hash3, type BlockoutBox } from '../../region/sketch';

type Box = BlockoutBox;
const boxes: Box[] = [];
/** Adds a box; the walls' boxes stand on the plain's level (the river's banks dip under them). */
const add = (b: Box): void => {
  boxes.push(b.lane === 'hub.walls' && b.base === undefined ? { ...b, base: FLOOR } : b);
};

/** The city's frame (region metres): walls, centre, the plain's level, wall height and thickness. */
export const CITY = {
  x0: 1020,
  x1: 1900,
  y0: 1280,
  y1: 2020,
  cx: 1450,
  cy: 1650,
  floor: 22,
  wallH: 9,
  wallT: 8,
} as const;
const { x0: X0, x1: X1, y0: Y0, y1: Y1, cx: CX, cy: CY, floor: FLOOR, wallH: WH, wallT: WT } = CITY;
const P = 2;
const N = 'ลานศิลาเก้ากระบี่';

/** ลำน้ำศิลา's centreline through the city: a straight reach (1660, 2040) → (1860, 1260). */
export const bankX = (y: number): number => 1660 + (2040 - y) * (200 / 780);

/** A rectangle [x0, x1, y0, y1]. */
type Rect = readonly [number, number, number, number];

/** A box from a rectangle. */
const rectBox = (r: Rect): { x: number; y: number; w: number; d: number } => ({
  x: (r[0] + r[1]) / 2,
  y: (r[2] + r[3]) / 2,
  w: r[1] - r[0],
  d: r[3] - r[2],
});

/** A straight run from `a` to `b` along x or y, `t` thick, broken at `gaps`: boxes `<id>.<n>`. */
function run(
  id: string,
  name: string,
  lane: string,
  axis: 'x' | 'y',
  fixed: number,
  a: number,
  b: number,
  t: number,
  h: number,
  gaps: readonly (readonly [number, number])[],
  kind: Box['kind'] = 'wall',
  place = P,
): void {
  const cuts = [a, ...gaps.flat(), b];
  let n = 0;
  for (let k = 0; k + 1 < cuts.length; k += 2) {
    const u0 = cuts[k]!;
    const u1 = cuts[k + 1]!;
    if (u1 <= u0) continue;
    const c = (u0 + u1) / 2;
    const L = u1 - u0;
    add({
      id: `${id}.${++n}`,
      place,
      name,
      lane,
      kind,
      h,
      ...(axis === 'x' ? { x: c, y: fixed, w: L, d: t } : { x: fixed, y: c, w: t, d: L }),
    });
  }
}

/**
 * A courtyard compound: a garden wall (1 m, 2.6 m high) with a 6 m gate on side `gate`, and the
 * main hall at the back (`<id>` itself), as the plan's `compound`.
 */
function compound(id: string, name: string, lane: string, r: Rect, gate: Facing, hall: { h?: number; kind?: Box['kind']; name?: string } = {}): void {
  const [x0, x1, y0, y1] = r;
  const gx = Math.round((x0 + x1) / 2);
  const gy = Math.round((y0 + y1) / 2);
  const ns = gate === 's' || gate === 'n';
  const w = x1 - x0;
  const d = y1 - y0;
  const depth = Math.min(16, Math.round((ns ? d : w) * 0.26));
  const span = Math.min(48, Math.round((ns ? w : d) * 0.55));
  const hb: Rect = ns
    ? [gx - span / 2, gx + span / 2, gate === 's' ? y0 + 5 : y1 - 5 - depth, gate === 's' ? y0 + 5 + depth : y1 - 5]
    : [gate === 'e' ? x0 + 5 : x1 - 5 - depth, gate === 'e' ? x0 + 5 + depth : x1 - 5, gy - span / 2, gy + span / 2];
  const wn = `${name} กำแพงสวน`;
  const H = 2.6;
  run(`${id}.wall.n`, wn, lane, 'x', y0 + 0.5, x0, x1, 1, H, gate === 'n' ? [[gx - 3, gx + 3]] : []);
  run(`${id}.wall.s`, wn, lane, 'x', y1 - 0.5, x0, x1, 1, H, gate === 's' ? [[gx - 3, gx + 3]] : []);
  run(`${id}.wall.w`, wn, lane, 'y', x0 + 0.5, y0 + 1, y1 - 1, 1, H, gate === 'w' ? [[gy - 3, gy + 3]] : []);
  run(`${id}.wall.e`, wn, lane, 'y', x1 - 0.5, y0 + 1, y1 - 1, 1, H, gate === 'e' ? [[gy - 3, gy + 3]] : []);
  add({ id, place: P, name: hall.name ?? name, lane, kind: hall.kind ?? 'house', ...rectBox(hb), h: hall.h ?? 7 });
}

// ── Walls and gates (9 m, 8 m thick; the wall walk is 3.2 km) ─────────────────────────────────
// Gates 14 m wide: north and south on ถนนหลวง (x 1443–1457), east and west on the avenue
// (y 1643–1657), south-west to the bandit hills and Shaolin (y 1853–1867). Water gates where
// ลำน้ำศิลา passes the north and south walls.
{
  const L = 'hub.walls';
  const ng: readonly [number, number] = [CX - 7, CX + 7];
  const wn = Math.round(bankX(Y0 + WT / 2));
  const ws = Math.round(bankX(Y1 - WT / 2));
  // (The north water gate's east tower meets the north-east corner tower: no wall between.)
  run('hub.wall.n', `${N} กำแพงเหนือ`, L, 'x', Y0 + WT / 2, X0, X1, WT, WH, [ng, [wn - 22, X1]]);
  run('hub.wall.s', `${N} กำแพงใต้`, L, 'x', Y1 - WT / 2, X0, X1, WT, WH, [ng, [ws - 22, ws + 22]]);
  run('hub.wall.e', `${N} กำแพงตะวันออก`, L, 'y', X1 - WT / 2, Y0 + WT, Y1 - WT, WT, WH, [[CY - 7, CY + 7]]);
  run('hub.wall.w', `${N} กำแพงตะวันตก`, L, 'y', X0 + WT / 2, Y0 + WT, Y1 - WT, WT, WH, [
    [CY - 7, CY + 7],
    [Y1 - 167, Y1 - 153],
  ]);
  // Corner towers (perches 12 m up).
  const corners: [string, number, number][] = [
    ['nw', X0 + 8, Y0 + 8],
    ['ne', X1 - 8, Y0 + 8],
    ['sw', X0 + 8, Y1 - 8],
    ['se', X1 - 8, Y1 - 8],
  ];
  for (const [k, x, y] of corners) add({ id: `hub.wall.corner.${k}`, place: P, name: `${N} หอมุม`, lane: L, kind: 'tower', x, y, w: 16, d: 16, h: 12 });
  // Gate towers either side of each gate (13 m), and of the water gates (12 m).
  const towers: [string, string, number, number, boolean, number][] = [
    ['hub.gate.n', 'ประตูเหนือ', CX, Y0 + WT / 2, true, 13],
    ['hub.gate.s', 'ประตูใต้', CX, Y1 - WT / 2, true, 13],
    ['hub.gate.e', 'ประตูตะวันออก', X1 - WT / 2, CY, false, 13],
    ['hub.gate.w', 'ประตูตะวันตก', X0 + WT / 2, CY, false, 13],
    ['hub.gate.sw', 'ประตูตะวันตกเฉียงใต้', X0 + WT / 2, Y1 - 160, false, 13],
    ['hub.watergate.n', 'ประตูน้ำเหนือ', wn, Y0 + WT / 2, true, 12],
    ['hub.watergate.s', 'ประตูน้ำใต้', ws, Y1 - WT / 2, true, 12],
  ];
  for (const [id, name, x, y, ns, h] of towers) {
    const gap = id.includes('water') ? 22 : 7;
    for (const [k, s] of [
      [1, -1],
      [2, 1],
    ] as const)
      add({
        id: `${id}.${k}`,
        place: P,
        name: `${N} ${name}`,
        lane: L,
        kind: 'gate',
        ...(ns ? { x: x + s * (gap + 4), y, w: 8, d: 12 } : { x, y: y + s * (gap + 4), w: 12, d: 8 }),
        h,
      });
  }
  // Barbicans (瓮城) outside the north and south gates: 80 × 40 m, the outer gate on the east
  // (x 1470–1482), so the way in turns twice.
  for (const [k, yo, outer] of [
    ['n', Y0 - 40, Y0 - 37],
    ['s', Y1, Y1 + 37],
  ] as const) {
    const id = `hub.barbican.${k}`;
    const name = `${N} ${k === 'n' ? 'ป้อมปราการประตูเหนือ' : 'ป้อมปราการประตูใต้'}`;
    add({ id: `${id}.w`, place: P, name, lane: L, kind: 'wall', x: CX - 37, y: yo + 20, w: 6, d: 40, h: WH });
    add({ id: `${id}.e`, place: P, name, lane: L, kind: 'wall', x: CX + 37, y: yo + 20, w: 6, d: 40, h: WH });
    run(`${id}.outer`, name, L, 'x', outer, CX - 34, CX + 34, 6, WH, [[CX + 20, CX + 32]]);
  }
  // Stairs up the wall's inner face beside four gates (9 m over 28 m), onto the wall walk.
  const stairs: [string, number, number, number, number, Facing][] = [
    ['n', CX + 34, Y0 + WT + 3, 28, 6, 'e'],
    ['s', CX + 34, Y1 - WT - 3, 28, 6, 'e'],
    ['e', X1 - WT - 3, CY + 34, 6, 28, 's'],
    ['w', X0 + WT + 3, CY - 34, 6, 28, 'n'],
  ];
  for (const [k, x, y, w, d, down] of stairs)
    add({ id: `hub.wall.stair.${k}`, place: P, name: `${N} บันไดขึ้นกำแพง`, lane: L, kind: 'ramp', down, x, y, w, d, h: WH, base: FLOOR });
}

// ── ลานศิลาใหญ่ and the civic quarter ───────────────────────────────────────────────────────
{
  const L = 'hub.civic';
  add({ id: 'hub.plaza', place: P, name: 'ลานศิลาใหญ่', lane: L, kind: 'landing', x: CX, y: CY, w: 260, d: 200, h: 0, base: FLOOR, note: '260 × 200 m of paving where ถนนหลวง and the avenue cross' });
  add({ id: 'hub.stone.base', place: P, name: 'ลานศิลาใหญ่ ฐานศิลา', lane: L, kind: 'terrace', x: CX, y: CY, w: 28, d: 28, h: 0.3, base: FLOOR });
  add({ id: 'hub.stone', place: P, name: 'ศิลาเก้ากระบี่', lane: L, kind: 'tower', x: CX, y: CY, w: 4, d: 4, h: 9, on: 'hub.stone.base', note: 'the nine-sword stone; an updraft to 60 m round it' });
  // The drum tower straddles the avenue on two plinths (the passage between them, y 1635–1665).
  add({ id: 'hub.drumtower.n', place: P, name: 'หอกลอง ฐานเหนือ', lane: L, kind: 'tower', x: CX + 150, y: CY - 19, w: 16, d: 8, h: 12 });
  add({ id: 'hub.drumtower.s', place: P, name: 'หอกลอง ฐานใต้', lane: L, kind: 'tower', x: CX + 150, y: CY + 19, w: 16, d: 8, h: 12 });
  add({ id: 'hub.belltower', place: P, name: 'หอระฆัง', lane: L, kind: 'tower', x: CX - 160, y: CY - 32, w: 16, d: 16, h: 16 });
  add({ id: 'hub.plaza.office', place: P, name: 'ลานหน้าศาลาว่าการ', lane: L, kind: 'landing', x: CX, y: Y0 + 165, w: 180, d: 70, h: 0, base: FLOOR });
  compound('hub.office', 'ศาลาว่าการ', L, [CX - 150, CX - 30, Y0 + 20, Y0 + 122], 's', { kind: 'hall', h: 11 });
  add({ id: 'hub.jail', place: P, name: 'คุก', lane: L, kind: 'house', x: CX - 133, y: Y0 + 98, w: 26, d: 16, h: 5 });
  compound('hub.keepers', 'ศาลผู้เฝ้าศิลา', L, [CX + 30, CX + 150, Y0 + 20, Y0 + 122], 's', { kind: 'hall', h: 11 });
  // The coach yard inside the north gate (the station spot (1494, 1512) stays clear).
  add({ id: 'hub.station', place: P, name: 'ท่ารถม้าลานศิลา', lane: 'roads', kind: 'station', x: 1494, y: 1524, w: 10, d: 6, h: 4, note: 'station spot (1494, 1512) stays clear' });
  add({ id: 'hub.stable', place: P, name: 'ท่ารถม้าลานศิลา คอกม้า', lane: 'roads', kind: 'house', x: 1543.5, y: 1499, w: 27, d: 12, h: 4 });
  // The lotus lake's pavilion on its island (north-west quarter).
  add({ id: 'hub.lotus.pavilion', place: P, name: 'ศาลากลางบึงบัว', lane: L, kind: 'hall', x: 1215, y: 1360, w: 14, d: 14, h: 6, base: FLOOR });
  add({ id: 'hub.parklake.pavilion', place: P, name: 'ศาลาริมบึงกก', lane: L, kind: 'hall', x: 1242, y: 1848, w: 12, d: 12, h: 5, base: FLOOR });
}

// ── The martial quarter: the training ground and the arena ───────────────────────────────────
{
  const L = 'hub.martial';
  const yard: Rect = [1170, 1300, CY + 30, CY + 130];
  add({ id: 'hub.yard', place: P, name: 'ลานฝึกลานศิลา', lane: L, kind: 'terrace', ...rectBox(yard), h: 0.4, base: FLOOR });
  const fence = `ลานฝึกลานศิลา รั้ว`;
  run('hub.yard.fence.n', fence, L, 'x', yard[2] + 0.5, yard[0], yard[1], 1, 1.2, [[1230, 1240]], 'camp');
  run('hub.yard.fence.s', fence, L, 'x', yard[3] - 0.5, yard[0], yard[1], 1, 1.2, [[1230, 1240]], 'camp');
  run('hub.yard.fence.w', fence, L, 'y', yard[0] + 0.5, yard[2] + 1, yard[3] - 1, 1, 1.2, [[1725, 1735]], 'camp');
  run('hub.yard.fence.e', fence, L, 'y', yard[1] - 0.5, yard[2] + 1, yard[3] - 1, 1, 1.2, [[1725, 1735]], 'camp');
  add({ id: 'hub.yard.armoury', place: P, name: 'โรงเก็บอาวุธ', lane: L, kind: 'house', x: 1219, y: CY + 40, w: 18, d: 10, h: 4 });
  add({ id: 'hub.yard.stage', place: P, name: 'ลานฝึกลานศิลา เวทีฝึก', lane: L, kind: 'terrace', x: 1279, y: CY + 109, w: 18, d: 18, h: 1.4, base: FLOOR });
  // The arena: a walled ring 80 × 112 m, its gate north.
  const ar: Rect = [1040, 1120, CY + 14, CY + 126];
  add({ id: 'hub.arena', place: P, name: 'ลานประลอง', lane: L, kind: 'terrace', ...rectBox(ar), h: 0.4, base: FLOOR });
  const ring = 'ลานประลอง อัฒจันทร์';
  run('hub.arena.wall.n', ring, L, 'x', ar[2] + 2, ar[0], ar[1], 4, 4, [[1074, 1086]]);
  run('hub.arena.wall.s', ring, L, 'x', ar[3] - 2, ar[0], ar[1], 4, 4, []);
  run('hub.arena.wall.w', ring, L, 'y', ar[0] + 2, ar[2] + 4, ar[3] - 4, 4, 4, []);
  run('hub.arena.wall.e', ring, L, 'y', ar[1] - 2, ar[2] + 4, ar[3] - 4, 4, 4, []);
}

// ── ลำน้ำศิลา: quays (ลานท่าน้ำ) and three bridges ─────────────────────────────────────────────
const BRIDGES = [
  { id: 'hub.bridge.n', name: 'สะพานเหนือ', y: Y0 + 120, w: 16 },
  { id: 'hub.rainbow', name: 'สะพานรุ้ง', y: CY, w: 30 },
  { id: 'hub.bridge.s', name: 'สะพานใต้', y: Y1 - 140, w: 20 },
] as const;
{
  const L = 'hub.river';
  // Stone quays 12 m wide on both banks, 24–36 m off the centreline, in 40 m lengths.
  let n = 0;
  for (let y = Y0 + 40; y <= Y1 - 40; y += 40) {
    if (BRIDGES.some((b) => Math.abs(b.y - y) < 20 + b.w / 2)) continue;
    n++;
    const c = bankX(y);
    for (const [s, side] of [
      [-1, 'w'],
      [1, 'e'],
    ] as const)
      add({ id: `hub.quay.${side}.${n}`, place: P, name: 'ลานท่าน้ำ', lane: L, kind: 'landing', x: Math.round(c + s * 30), y, w: 12, d: 40, h: 0, base: FLOOR });
  }
  for (const b of BRIDGES) {
    const c = Math.round(bankX(b.y));
    if (b.id === 'hub.rainbow') {
      // The arch: two ramps rising 3 m over 30 m to a 40 m crown at 25 m.
      add({ id: `${b.id}.ramp.w`, place: P, name: b.name, lane: L, kind: 'ramp', down: 'w', x: c - 35, y: b.y, w: 30, d: b.w, h: 3, base: FLOOR });
      add({ id: b.id, place: P, name: b.name, lane: L, kind: 'landing', x: c, y: b.y, w: 40, d: b.w, h: 0, base: FLOOR + 3, note: 'the crown; an updraft to 45 m beside it over the water' });
      add({ id: `${b.id}.ramp.e`, place: P, name: b.name, lane: L, kind: 'ramp', down: 'e', x: c + 35, y: b.y, w: 30, d: b.w, h: 3, base: FLOOR });
    } else add({ id: b.id, place: P, name: b.name, lane: L, kind: 'landing', x: c, y: b.y, w: 80, d: b.w, h: 0.2, base: FLOOR });
  }
}

// ── The east market, the shop, the east bank (the inn, the opera house) ─────────────────────
const MARKET: Rect = [CX + 175, Math.round(bankX(1500) - 46), 1450, 1540];
const SHOP: Rect = [CX + 175, CX + 235, CY + 30, CY + 80];
const INN: Rect = [Math.round(bankX(1600) + 46), X1 - WT - 16, CY - 80, CY - 20];
const OPERA: Rect = [1790, X1 - WT - 16, 1720, 1820];
{
  const L = 'hub.market';
  add({ id: 'hub.market.east', place: P, name: 'ตลาดตะวันออก', lane: L, kind: 'landing', ...rectBox(MARKET), h: 0, base: FLOOR });
  const mx = (MARKET[0] + MARKET[1]) / 2;
  [1468, 1495, 1522].forEach((y, k) =>
    add({ id: `hub.market.stalls.${k + 1}`, place: P, name: 'ตลาดตะวันออก แถวแผงลอย', lane: L, kind: 'tent', x: mx, y, w: MARKET[1] - MARKET[0] - 16, d: 4, h: 2.4 }),
  );
  compound('hub.shop', 'ร้านค้าลานศิลา', L, SHOP, 'n', { h: 7 });
  compound('hub.inn', 'โรงเตี๊ยมริมน้ำ', 'hub.eastbank', INN, 'w', { kind: 'hall', h: 11 });
  compound('hub.opera', 'โรงงิ้วริมน้ำ', 'hub.eastbank', OPERA, 'w', { kind: 'hall', h: 12 });
}

// ── Garden compounds (เรือนสวน) on a loose grid wherever the ground is free ─────────────────────
{
  const L = 'hub.homes';
  const m = 6;
  const lotus: Rect = [1215 - 80, 1215 + 80, 1360 - 54, 1360 + 54];
  const park: Rect = [1160 - 80, 1250, 1850 - 40, 1850 + 40];
  const taken: Rect[] = [
    // The ring road inside the walls.
    [X0, X1, Y0, Y0 + WT + 10],
    [X0, X1, Y1 - WT - 10, Y1],
    [X0, X0 + WT + 10, Y0, Y1],
    [X1 - WT - 10, X1, Y0, Y1],
    // Streets: ถนนหลวง, the avenue, the south-west street, two lanes.
    [CX - 20, CX + 20, Y0, Y1],
    [X0, X1, CY - 15, CY + 15],
    [X0, CX, Y1 - 175, Y1 - 145],
    [1150, 1162, Y0, Y1],
    [X0, CX, 1420, 1432],
    // Plazas and the civic compounds, the coach yard, the bell tower.
    [CX - 130, CX + 130, CY - 100, CY + 100],
    [CX - 90, CX + 90, Y0 + 130, Y0 + 200],
    [CX - 150, CX - 30, Y0 + 20, Y0 + 122],
    [CX + 30, CX + 150, Y0 + 20, Y0 + 122],
    [1480, 1560, 1490, 1534],
    [CX - 168, CX - 152, CY - 40, CY - 24],
    // Lakes, the martial quarter.
    lotus,
    park,
    [1170, 1300, CY + 30, CY + 130],
    [1040, 1120, CY + 14, CY + 126],
    // The market, the shop, the inn, the opera house.
    MARKET,
    SHOP,
    INN,
    OPERA,
  ];
  const free = (r: Rect): boolean =>
    !taken.some((t) => r[0] < t[1] + m && r[1] > t[0] - m && r[2] < t[3] + m && r[3] > t[2] - m) &&
    // Clear of the river and its quays (36 m off the centreline, plus the margin).
    [r[2], r[3]].every((y) => r[1] < bankX(y) - 36 - m || r[0] > bankX(y) + 36 + m);
  let n = 0;
  for (let y = Y0 + 24; y + 38 <= Y1 - 24; y += 38 + 16)
    for (let x = X0 + 24; x + 46 <= X1 - 24; x += 46 + 16) {
      const w = Math.round(46 * (0.75 + hash3(x, y, 41) * 0.25));
      const d = Math.round(38 * (0.75 + hash3(x, y, 42) * 0.25));
      const r: Rect = [x, x + w, y, y + d];
      if (hash3(x, y, 43) < 0.2 || !free(r)) continue;
      taken.push(r);
      compound(`hub.home.${++n}`, 'เรือนสวน', L, r, hash3(x, y, 44) < 0.5 ? 's' : 'n', { h: 6 });
    }
}

// ── Outside the walls: the fair, the south suburb, the boss's ground, the shrine ─────────────────
{
  const L = 'hub.suburbs';
  let k = 0;
  for (let x = CX - 90; x <= CX + 120; x += 30) {
    if (Math.abs(x - 1476) < 20) continue;
    add({ id: `hub.fair.tent.${++k}`, place: P, name: 'ตลาดนัดนอกประตูเหนือ เต็นท์', lane: L, kind: 'tent', x, y: Y0 - 75, w: 10, d: 10, h: 3 });
  }
  const homes: Rect[] = [
    [1376, 1416, 2080, 2114],
    [1376, 1416, 2128, 2162],
    [1494, 1534, 2080, 2114],
    [1494, 1534, 2128, 2162],
  ];
  homes.forEach((r, i) => compound(`hub.suburb.s.${i + 1}`, 'ชานเมืองใต้ เรือน', L, r, i < 2 ? 'e' : 'w', { h: 5 }));
  // ลานจอมมาร: nine steles round a 56 m ground, open to the east (its path in).
  for (let i = 0; i < 9; i++) {
    const a = ((i + 0.5) / 9) * Math.PI * 2;
    add({ id: `hub.bossground.stele.${i + 1}`, place: P, name: 'ลานจอมมาร ศิลาจารึก', lane: L, kind: 'stone', x: Math.round(1320 + Math.cos(a) * 28), y: Math.round(1150 + Math.sin(a) * 28), w: 2, d: 2, h: 3 });
  }
  add({ id: 'hub.liu.shrine', place: P, name: 'ศาลร้างกลางทุ่ง', lane: L, kind: 'house', x: 930, y: 1700, w: 6, d: 6, h: 3 });
}

// ── 15 · เจดีย์เก้าชั้น on its hill outside the west gate: nine tiers, 7 m each, 63 m ───────────
for (let k = 0; k < 9; k++)
  add({
    id: `hub.pagoda.tier.${k + 1}`,
    place: 15,
    name: `เจดีย์เก้าชั้น ชั้น ${k + 1}`,
    lane: 'hub.pagoda',
    kind: 'tier',
    x: 880,
    y: 1560,
    w: 26 - 2 * k,
    d: 26 - 2 * k,
    h: 7 * (k + 1),
    note: k < 8 ? 'its rim is a perch 7 m over the tier below' : 'the top, 63 m over its 32 m hill',
  });
add({ id: 'hub.pagoda.hall', place: 15, name: 'วัดเจดีย์ วิหาร', lane: 'hub.pagoda', kind: 'hall', x: 880, y: 1522, w: 24, d: 12, h: 7 });

/** The city's boxes, in stamping order (later win where they overlap). */
export const YANYU_CITY_BOXES: readonly BlockoutBox[] = boxes;
